/** @format */

import { useRef, useEffect, useState } from 'react';
import * as echarts from 'echarts';
import {
  Box,
  Paper,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { COMPONENT_TITLE_SX } from '../homeStyles';
import { getBARLine } from '../../../../Services/service.home';
import VectorToggler from '../../../Organisms/VectorToggler/VectorToggler';
import { FILTER_NAMES, useAppContext } from '../../../../store/store.app';
import { buildPayload } from '../../../Library/appfilter.lib';
import useMaxDate from '../../../Pages/BizBarometr/hooks/useMaxDate';

const colors = [
  '#5470c6',
  '#be1e2d',
  '#91cc75',
  '#fac858',
  '#ee6666',
  '#73c0de',
  '#3ba272',
  '#fc8452',
  '#ea7ccc',
];

const GRAPH_TYPES = [
  { key: 'line', title: 'Line' },
  { key: 'table', title: 'Table' },
];

function parseDate(value) {
  if (!value) {
    return null;
  }

  const cleanValue = String(value).trim();

  const match = cleanValue.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);

  if (match) {
    const date = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );

    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(cleanValue);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateLabel(value) {
  const cleanValue = String(value ?? '')
    .trim()
    .replace(/\s+/g, '');

  const date = parseDate(cleanValue);

  if (!date) {
    return cleanValue.replace(/\s+/g, '');
  }

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);

  return `${day}${month}'${year}`;
}

function getWeeklyLabelIndexes(values) {
  if (!values?.length) {
    return [];
  }

  if (values.length === 1) {
    return [0];
  }

  const dates = values.map(parseDate);

  if (dates.some((date) => !date)) {
    const labelCount = Math.min(7, values.length);
    const indexes = [];

    for (let i = 0; i < labelCount; i += 1) {
      const index = Math.round(
        (i * (values.length - 1)) / (labelCount - 1)
      );

      indexes.push(index);
    }

    return [...new Set(indexes)];
  }

  const indexes = [0];
  let lastDate = dates[0];

  for (let index = 1; index < dates.length; index += 1) {
    const currentDate = dates[index];

    if (!currentDate) {
      continue;
    }

    const difference =
      (currentDate.getTime() - lastDate.getTime()) /
      (1000 * 60 * 60 * 24);

    if (difference >= 7) {
      indexes.push(index);
      lastDate = currentDate;
    }
  }

  if (indexes[indexes.length - 1] !== values.length - 1) {
    indexes.push(values.length - 1);
  }

  return [...new Set(indexes)];
}

export default function BARGraph() {
  const [data, setData] = useState(null);

  const [selectedGraphType, setSelectedGraphType] = useState(
    GRAPH_TYPES[0]
  );

  const {
    appStore: { appFilters },
  } = useAppContext();

  const PARAMETER = appFilters[FILTER_NAMES.PARAMETER];

  const DATERANGE = appFilters[FILTER_NAMES.DATERANGE];

  const maxDate = useMaxDate();

  const hotel = PARAMETER ? PARAMETER[0] : null;

  useEffect(() => {
    if (!PARAMETER || !DATERANGE || !maxDate) {
      return;
    }

    const fetchData = async () => {
      try {
        const payload = buildPayload(appFilters, [
          FILTER_NAMES.PARAMETER,
          FILTER_NAMES.DATERANGE,
        ]);

        const maxDatePayload = {
          max_date: maxDate.format('YYYY-MM-DD'),
        };

        const { data: barLineData } = await getBARLine({
          ...payload,
          ...maxDatePayload,
        });

        setData(barLineData);
      } catch (error) {
        console.error('Error fetching BAR data:', error);
      }
    };

    fetchData();
  }, [DATERANGE, PARAMETER, maxDate]);

  return (
    <Box sx={{ my: 2, width: '100%', minWidth: 0 }}>
      <Typography
        variant="h5"
        textAlign="center"
        color="text.disabled"
        sx={{
          ...COMPONENT_TITLE_SX,
          overflowWrap: 'anywhere',
        }}
      >
        Best Available Rates (BAR) in Public Domain
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          backgroundColor: '#f2f2eb',
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          paddingTop: 1,
          borderRadius: 1,
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 1,
            px: { xs: 1, sm: 0 },
          }}
        >
          <VectorToggler
            vectorList={GRAPH_TYPES}
            setSelectedVector={setSelectedGraphType}
            selectedVector={selectedGraphType}
          />
        </Box>

        {selectedGraphType.key === 'line' && (
          <LineGraph data={data} hotel={hotel} />
        )}

        {selectedGraphType.key === 'table' && <TableView data={data} />}
      </Paper>
    </Box>
  );
}

function LineGraph({ data, hotel }) {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const [selectedHotel, setSelectedHotel] = useState('');
  const [hiddenSeries, setHiddenSeries] = useState([]);

  const hotelName = hotel?.selected || '';

  const hotelOptions = data?.yAxis || [];

  const sortedYAxis = [...hotelOptions].sort((a, b) => {
    const aIsOcc = a.includes('Occ%');
    const bIsOcc = b.includes('Occ%');

    if (aIsOcc && !bIsOcc) {
      return -1;
    }

    if (!aIsOcc && bIsOcc) {
      return 1;
    }

    const aMatch = a
      .toLowerCase()
      .includes(String(hotelName).toLowerCase());

    const bMatch = b
      .toLowerCase()
      .includes(String(hotelName).toLowerCase());

    if (aMatch && !bMatch) {
      return -1;
    }

    if (!aMatch && bMatch) {
      return 1;
    }

    return 0;
  });

  const lineColorFor = (name) => {
    if (name.includes('Occ%')) {
      return '#9b59b6';
    }

    const index = sortedYAxis.indexOf(name);

    return colors[(index >= 0 ? index : 0) % colors.length];
  };

  useEffect(() => {
    if (!hotelOptions.length) {
      setSelectedHotel('');
      setHiddenSeries([]);
      return;
    }

    setSelectedHotel((previous) =>
      previous && hotelOptions.includes(previous) ? previous : ''
    );

    setHiddenSeries((previous) =>
      previous.filter((name) => hotelOptions.includes(name))
    );
  }, [data]);

  useEffect(() => {
    if (!data || !hotelName || !chartRef.current) {
      return;
    }

    if (chartInstanceRef.current) {
      chartInstanceRef.current.dispose();
      chartInstanceRef.current = null;
    }

    echarts.dispose(chartRef.current);

    const chart = echarts.init(chartRef.current);

    chartInstanceRef.current = chart;

    const { xAxis, yAxis: originalYAxis, Success = [] } = data;

    const yAxis = [...(originalYAxis || [])].sort((a, b) => {
      const aIsOcc = a.includes('Occ%');
      const bIsOcc = b.includes('Occ%');

      if (aIsOcc && !bIsOcc) {
        return -1;
      }

      if (!aIsOcc && bIsOcc) {
        return 1;
      }

      const aMatch = a
        .toLowerCase()
        .includes(String(hotelName).toLowerCase());

      const bMatch = b
        .toLowerCase()
        .includes(String(hotelName).toLowerCase());

      if (aMatch && !bMatch) {
        return -1;
      }

      if (!aMatch && bMatch) {
        return 1;
      }

      return 0;
    });

    const xAxisSeries = Success.map((item) => item[xAxis]);

    const weeklyLabelIndexes = getWeeklyLabelIndexes(xAxisSeries);

    const labelIndexes = new Set(weeklyLabelIndexes);

    const hasFocus = selectedHotel !== '';

    const series = yAxis.map((item, index) => {
      const isOccupancy = item.includes('Occ%');

      const isFocused = item === selectedHotel;

      const isHidden = hiddenSeries.includes(item);

      let opacity = 1;

      if (isHidden) {
        opacity = 0;
      } else if (hasFocus && !isFocused) {
        opacity = 0.15;
      }

      return {
        name: item,
        type: 'line',
        smooth: true,
        symbol: 'none',
        yAxisIndex: isOccupancy ? 1 : 0,

        data: Success.map((row) => row[item]),

        itemStyle: {
          color: isOccupancy
            ? '#9b59b6'
            : colors[index % colors.length],
          opacity,
        },

        lineStyle: {
          width: hasFocus
            ? isFocused
              ? 3.5
              : 2
            : index === 1
              ? 3.2
              : 2,

          opacity,
        },

        ...(isOccupancy
          ? {
              areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  {
                    offset: 0,
                    color: isHidden
                      ? 'rgba(154, 96, 180, 0)'
                      : 'rgba(154, 96, 180, 0.55)',
                  },
                  {
                    offset: 1,
                    color: 'rgba(0, 123, 255, 0.05)',
                  },
                ]),
              },
            }
          : {}),

        z: isFocused ? 30 : 10,

        emphasis: {
          focus: 'series',
        },

        animation: true,
      };
    });

    chart.setOption({
      animation: true,

      tooltip: {
        trigger: 'axis',

        axisPointer: {
          type: 'cross',
        },

        formatter(params) {
          if (!params?.length) {
            return '';
          }

          const visibleParams = params.filter(
            (item) => !hiddenSeries.includes(item.seriesName)
          );

          if (!visibleParams.length) {
            return '';
          }

          let result = `${formatDateLabel(
            visibleParams[0].axisValue
          )}<br/>`;

          visibleParams.forEach((item) => {
            const isFocused = item.seriesName === selectedHotel;

            const isOccupancy = item.seriesName.includes('Occ%');

            const value = isOccupancy
              ? item.value
              : typeof item.value === 'number'
                ? item.value.toLocaleString('en-IN', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })
                : item.value ?? '0';

            result += `
              <div
                style="
                  display:flex;
                  justify-content:space-between;
                  align-items:center;
                  gap:20px;
                  opacity:${hasFocus && !isFocused ? 0.4 : 1};
                "
              >
                <div>
                  ${item.marker}

                  <span
                    style="
                      font-weight:${isFocused ? '700' : '400'};
                    "
                  >
                    ${item.seriesName}
                  </span>
                </div>

                <span
                  style="
                    font-family:'Roboto Condensed', sans-serif;
                    font-variant-numeric:tabular-nums;
                    font-weight:${isFocused ? '700' : '400'};
                  "
                >
                  ${value}
                </span>
              </div>
            `;
          });

          return result;
        },
      },

      legend: {
        show: false,
      },

      grid: {
        left: '5%',
        right: '5%',
        top: '10%',
        bottom: '16%',
        containLabel: true,
      },

      xAxis: [
        {
          type: 'category',

          boundaryGap: false,

          data: xAxisSeries,

          axisLabel: {
            show: false,
          },

          axisTick: {
            show: false,
          },

          axisLine: {
            show: true,
            onZero: false,
          },
        },

        {
          type: 'category',

          position: 'bottom',

          boundaryGap: false,

          data: xAxisSeries,

          axisLabel: {
            show: true,

            interval: 0,

            margin: 9,

            hideOverlap: true,

            align: 'center',

            fontSize: 11,

            color: '#555',

            width: 60,

            overflow: 'truncate',

            formatter(value, index) {
              if (!labelIndexes.has(index)) {
                return '';
              }

              return formatDateLabel(value);
            },
          },

          axisTick: {
            show: true,

            interval(index) {
              return labelIndexes.has(index);
            },

            length: 6,

            alignWithLabel: true,

            lineStyle: {
              width: 1.5,
              color: '#777',
            },
          },

          axisLine: {
            show: true,

            lineStyle: {
              color: '#aaa',
              width: 1,
            },
          },

          splitLine: {
            show: true,

            interval(index) {
              return labelIndexes.has(index);
            },

            lineStyle: {
              color: '#ddd',
              width: 1,
              type: 'dashed',
            },
          },
        },
      ],

      yAxis: [
        {
          type: 'value',

          name: 'Rates',

          axisLabel: {
            formatter(value) {
              return Number(value).toLocaleString('en-IN', {
                maximumFractionDigits: 0,
              });
            },
          },
        },

        {
          type: 'value',

          name: 'Occ%',

          axisLabel: {
            formatter: '{value}',
          },
        },
      ],

      series,
    });

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener('resize', handleResize);

    let resizeObserver = null;

    if (typeof ResizeObserver !== 'undefined' && chartRef.current) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(chartRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);

      resizeObserver?.disconnect();

      chart.dispose();

      if (chartInstanceRef.current === chart) {
        chartInstanceRef.current = null;
      }
    };
  }, [data, hotelName, selectedHotel, hiddenSeries]);

  const toggleHotelFocus = (hotelOption) => {
    setSelectedHotel((previous) =>
      previous === hotelOption ? '' : hotelOption
    );
  };

  const toggleSeriesVisibility = (name) => {
    setHiddenSeries((previous) =>
      previous.includes(name)
        ? previous.filter((item) => item !== name)
        : [...previous, name]
    );
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          height: { xs: '260px', sm: '320px', md: '365px' },
        }}
      >
        <div
          ref={chartRef}
          style={{
            width: '100%',
            height: '100%',
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 0.7,
          px: 1.5,
          mt: 1.5,
          mb: 1,
        }}
      >
        {sortedYAxis.map((name) => {
          const lineColor = lineColorFor(name);

          const isFocused = selectedHotel === name;

          const isHidden = hiddenSeries.includes(name);

          const isDimmed =
            isHidden || (selectedHotel !== '' && !isFocused);

          return (
            <Box
              key={name}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 28,
                borderRadius: '7px',
                px: 0.65,
                pl: 0.8,
                backgroundColor: isFocused
                  ? `${lineColor}12`
                  : '#f2f2eb',

                border: isFocused
                  ? `1px solid ${lineColor}55`
                  : '1px solid #deded6',

                opacity: isDimmed ? 0.4 : 1,

                transition: 'all 0.2s ease',

                '&:hover': {
                  backgroundColor: isHidden
                    ? '#ebeae3'
                    : `${lineColor}10`,

                  borderColor: isHidden
                    ? '#d8d7d0'
                    : `${lineColor}45`,
                },
              }}
            >
              <Box
                component="span"
                onClick={() => toggleSeriesVisibility(name)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.65,
                  cursor: 'pointer',
                  userSelect: 'none',
                  minWidth: 0,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    position: 'relative',
                    width: 22,
                    height: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      height: isFocused ? 3 : 2,
                      borderRadius: '4px',
                      backgroundColor: lineColor,
                      transition: 'all 0.2s ease',
                    }}
                  />

                  <Box
                    component="span"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: isFocused ? 11 : 10,
                      height: isFocused ? 11 : 10,
                      borderRadius: '50%',
                      backgroundColor: lineColor,
                      border: `1px solid ${lineColor}`,
                      transition: 'all 0.2s ease',
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: isFocused ? 700 : 500,
                    lineHeight: 1,
                    color: isHidden
                      ? '#999'
                      : isFocused
                        ? lineColor
                        : '#444',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {name}
                </Typography>
              </Box>

              <Radio
                size="small"
                title={
                  isHidden
                    ? `Enable ${name} first`
                    : `Focus ${name}`
                }
                checked={isFocused}
                disabled={isHidden}
                onClick={() => {
                  if (!isHidden) {
                    toggleHotelFocus(name);
                  }
                }}
                sx={{
                  p: 0.35,
                  ml: 0.3,

                  '& .MuiSvgIcon-root': {
                    fontSize: 17,
                  },

                  '&.Mui-checked': {
                    color: lineColor,
                  },

                  '&:hover': {
                    backgroundColor: `${lineColor}12`,
                  },

                  '&.Mui-disabled': {
                    color: '#c8c8c8',
                  },
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function TableView({ data }) {
  if (!data) {
    return null;
  }

  const { xAxis, yAxis, Success } = data;

  return (
    <Box
      sx={{
        maxHeight: '400px',
        overflow: 'auto',
        width: '100%',
        minWidth: 0,
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Table
        size="small"
        aria-label="BAR table"
        sx={{
          minWidth: { xs: 0, sm: '100%' },
          width: '100%',
          borderCollapse: 'collapse',
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: '#f5f5f5',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <TableCell
              sx={{
                border: '1px solid #ddd',
                textAlign: 'center',
                backgroundColor: '#f5f5f5',
                fontSize: '11px',
                whiteSpace: 'nowrap',
              }}
            >
              {xAxis}
            </TableCell>

            {yAxis.map((hotel) => {
              const isOccPercent = hotel.includes('Occ%');
              const isJW = hotel.includes('JW');
              const isCYSiliguri = hotel
                .toLowerCase()
                .includes('cy siliguri');

              const shouldHighlightColumn =
                isOccPercent || isJW || isCYSiliguri;

              return (
                <TableCell
                  key={hotel}
                  sx={{
                    border: shouldHighlightColumn
                      ? '1px solid #b8c8df'
                      : '1px solid #ddd',

                    textAlign: 'center',

                    backgroundColor: shouldHighlightColumn
                      ? '#e0e8f5'
                      : '#f5f5f5',

                    fontSize: '11px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {hotel}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {Success.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              <TableCell
                sx={{
                  border: '1px solid #ddd',
                  textAlign: 'left',
                  backgroundColor: '#fafafa',
                  fontSize: '11px',
                  whiteSpace: 'nowrap',
                }}
              >
                {row[xAxis]}
              </TableCell>

              {yAxis.map((hotel) => {
                const isOccPercent = hotel.includes('Occ%');
                const isJW = hotel.includes('JW');

                const isCYSiliguri = hotel
                  .toLowerCase()
                  .includes('cy siliguri');

                const shouldHighlightColumn =
                  isOccPercent || isJW || isCYSiliguri;

                const rawValue = row[hotel];

                const formattedValue = isOccPercent
                  ? rawValue?.toLocaleString('en-IN', {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    }) || '0'
                  : rawValue?.toLocaleString('en-IN', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }) || '0';

                return (
                  <TableCell
                    key={hotel}
                    sx={{
                      border: shouldHighlightColumn
                        ? '1px solid #b8c8df'
                        : '1px solid #ddd',

                      padding: '8px',

                      textAlign: 'right',

                      fontSize: '11px',

                      whiteSpace: 'nowrap',

                      backgroundColor: shouldHighlightColumn
                        ? '#e0e8f5'
                        : 'transparent',

                      color: '#374151',

                      fontWeight: 400,
                    }}
                  >
                    <span className="data-number">
                      {formattedValue}
                    </span>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
